import { Component } from '@theme/component';

/**
 * `<bundle-reviews-widget>` — the REAL Judge.me review widget, refreshed per
 * product as the bundle selection changes.
 *
 * Judge.me's standard Shopify loader can't re-render a widget after page load,
 * but its cache-server preloader (cdnwidget.judge.me/widget_preloader.js)
 * exposes `jdgmCacheServer.reloadAll()`, which re-scans every
 * `.jdgm-review-widget[data-id]` on the page, re-fetches each widget BY its
 * `data-id`, fills it, and hydrates it natively. So to follow the viewed
 * product we just: set the placeholder's `data-id`, clear it, and reloadAll().
 * The result is the pixel-exact PDP widget (histogram, medals, verified,
 * photos, masonry) — switching per step.
 *
 * Refs: widget (the `.jdgm-review-widget[data-id]` placeholder).
 */
export class BundleReviewsWidgetComponent extends Component {
  requiredRefs = ['widget'];

  /** The product id (Judge.me data-id) currently loaded. */
  #currentId = '';
  /** True once the preloader + cache server are ready. */
  #ready = false;
  /** @type {number | undefined} */
  #debounceId;

  connectedCallback() {
    super.connectedCallback();
    // Don't trust the server-rendered seed id — let the first bundle:update set
    // the actually-viewed product, then reload once the preloader is ready.
    this.#currentId = '';
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    this.#ensurePreloader().then(() => {
      this.#ready = true;
      this.#reload();
    });
    this.#builder?.requestUpdate?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#debounceId);
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  /** Builder sits above this section, so look it up globally. */
  get #builder() {
    return this.closest('bundle-builder') || document.querySelector('bundle-builder');
  }

  /**
   * Ensure Judge.me identity + the cache-server preloader are present. The app
   * embed normally sets `window.jdgm.PUBLIC_TOKEN`; we backfill SHOP_DOMAIN /
   * PLATFORM (and load the preloader, which the Shopify theme app extension
   * doesn't include) so `jdgmCacheServer.reloadAll()` is available.
   */
  async #ensurePreloader() {
    const w = /** @type {any} */ (window);
    w.jdgm = w.jdgm || {};
    if (!w.jdgm.SHOP_DOMAIN && this.dataset.shop) w.jdgm.SHOP_DOMAIN = this.dataset.shop;
    if (!w.jdgm.PLATFORM) w.jdgm.PLATFORM = 'shopify';

    if (typeof w.jdgmCacheServer !== 'undefined') return;

    await new Promise((resolve) => {
      const existing = document.querySelector('script[src*="widget_preloader.js"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(undefined), { once: true });
        // If it already loaded, the poll below will pick jdgmCacheServer up.
        setTimeout(() => resolve(undefined), 0);
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://cdnwidget.judge.me/widget_preloader.js';
      s.async = true;
      s.addEventListener('load', () => resolve(undefined), { once: true });
      s.addEventListener('error', () => resolve(undefined), { once: true });
      document.head.appendChild(s);
    });

    // The preloader defines jdgmCacheServer shortly after load.
    for (let i = 0; i < 50 && typeof w.jdgmCacheServer === 'undefined'; i++) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  #onUpdate = () => {
    const builder = this.#builder;
    // @ts-ignore — getDraftSelection is defined on BundleBuilderComponent.
    const sel = builder?.getDraftSelection?.(builder.state?.currentStep);
    const id = sel?.product?.productId;
    if (id == null) return;
    const idStr = String(id);
    if (idStr === this.#currentId) return;
    this.#currentId = idStr;
    clearTimeout(this.#debounceId);
    this.#debounceId = window.setTimeout(() => this.#reload(), 200);
  };

  /** Point the placeholder at the current product and re-render via jdgm. */
  #reload() {
    const widget = this.refs.widget;
    const w = /** @type {any} */ (window);
    if (!widget || !this.#currentId || !this.#ready || typeof w.jdgmCacheServer === 'undefined') return;
    this.toggleAttribute('data-loading', true);
    widget.setAttribute('data-id', this.#currentId);
    widget.innerHTML = '';
    widget.classList.remove('jdgm--done-setup-widget', 'jdgm--done-setup');
    try {
      w.jdgmCacheServer.reloadAll();
    } catch (error) {
      console.warn('[bb-reviews-widget] reloadAll failed', error);
    }
    // Clear the loading veil once the widget has had time to hydrate.
    clearTimeout(this.#loadingClearId);
    this.#loadingClearId = window.setTimeout(() => this.removeAttribute('data-loading'), 1500);
  }

  /** @type {number | undefined} */
  #loadingClearId;
}

if (!customElements.get('bundle-reviews-widget')) {
  customElements.define('bundle-reviews-widget', BundleReviewsWidgetComponent);
}
